/**
 * SuperAdmin Data-Pipeline Health dashboard.
 *
 * One screen for the instrument-data lifecycle that backs every LTP lookup:
 *   master download → S3 → ECS in-memory cache → service health,
 * plus operator escape-hatches (trigger a master download, reload an ECS
 * cache) and a live broker-vs-MD LTP probe. SuperAdmin-only; the backend
 * (/admin/super/data-health/*) is gated server-side as well.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Activity, Home, RefreshCw, Download, Database, Server, HardDrive, CheckCircle2,
  ChevronRight, ChevronDown, X, Search, Send, AlertTriangle,
} from 'lucide-react';

import { useAuth } from '../context/AuthContext';
import StandardLayout from '../components/common/StandardLayout';
import PageHeader from '../components/common/PageHeader';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/Card';
import Button from '../components/ui/Button';
import { useToast } from '../components/common/ToastContainer';
import { useConfirm } from '../components/common/useConfirm';
import { cn } from '../utils/cn';
import Modal from '../components/ui/Modal';
import { BROKER_NAME, ACTION_TYPE, PRODUCT_TYPE, PRODUCT_TYPE_OPTIONS, EXCHANGE, EXCHANGE_OPTIONS } from '../constants/tradingConstants';
import dataHealthService, {
  DataHealth, CacheStat, LtpTestResult, InstrumentSearchResult, ServiceHealth, TaskHealth,
  PlaceOrderResult,
} from '../services/dataHealthService';

const REFRESH_BROKERS = ['zerodha', 'angel', 'noren'] as const;

// Shared form-control classes (single source for the LtpTester + DirectOrderPlacer inputs).
const FORM_INPUT_CLS = 'w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 placeholder-gray-500 dark:placeholder-gray-400';

// ───────────────────────── status derivation ─────────────────────────
type Health = 'healthy' | 'degraded' | 'critical' | 'unknown';

const PILL: Record<Health, string> = {
  healthy: 'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-200 border-green-200 dark:border-green-800',
  degraded: 'bg-yellow-50 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-200 border-yellow-200 dark:border-yellow-800',
  critical: 'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 border-red-200 dark:border-red-800',
  unknown: 'bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-700',
};

function cacheCount(stats: CacheStat | undefined): number | null {
  return stats && typeof stats.count === 'number' ? stats.count : null;
}

/** A service's cache_stats is either a per-broker map or an error shape. */
function asCacheMap(cs: any): Record<string, CacheStat> {
  if (cs && typeof cs === 'object' && !('status' in cs) && !('error' in cs)) {
    return cs as Record<string, CacheStat>;
  }
  return {};
}

/** Per-task rollup: total instruments across namespaces + latest load time. */
function taskSummary(t: TaskHealth): { total: number; namespaces: string[]; loadedAt: string | null } {
  const cs = asCacheMap(t.cache_stats);
  const namespaces = Object.keys(cs);
  const total = namespaces.reduce((s, k) => s + (cs[k]?.count || 0), 0);
  const loadedAt = namespaces.map((k) => cs[k]?.loaded_at).filter(Boolean).sort().pop() || null;
  return { total, namespaces, loadedAt };
}

/** True when replicas of a service disagree — unreachable task, or differing
 *  instrument totals (the exact "one task loaded, another stale" failure). */
function serviceDrift(svc?: ServiceHealth): boolean {
  const tasks = svc?.tasks || [];
  if (tasks.length < 2) return (svc?.reachable ?? tasks.length) < tasks.length;
  if ((svc?.reachable ?? 0) < (svc?.task_count ?? tasks.length)) return true;
  const totals = new Set(tasks.filter((t) => t.ok).map((t) => taskSummary(t).total));
  return totals.size > 1;
}

function deriveStatuses(d: DataHealth | null): { key: string; label: string; icon: React.ReactNode; status: Health; detail: string }[] {
  if (!d) {
    return [];
  }
  // Download — all refresh manifests succeeded?
  const refreshStates = REFRESH_BROKERS.map((b) => d.refresh[b]?.status);
  const download: Health = refreshStates.every((s) => s === 'success')
    ? 'healthy'
    : refreshStates.some((s) => s === 'error') ? 'critical' : 'degraded';

  // S3 — all master objects present?
  const s3States = REFRESH_BROKERS.map((b) => d.s3[b]?.exists);
  const s3: Health = s3States.every(Boolean) ? 'healthy' : s3States.some(Boolean) ? 'degraded' : 'critical';

  const brokerHealth = d.services.broker_api?.health?.status;
  const mdHealth = d.services.market_data?.health?.status;
  const brokerApi: Health = brokerHealth === 'healthy' ? 'healthy' : brokerHealth ? 'critical' : 'unknown';
  const marketData: Health = mdHealth === 'healthy' ? 'healthy' : mdHealth ? 'critical' : 'unknown';

  // Cache — both services report a non-empty per-broker map, AND replicas agree.
  const brokerMap = asCacheMap(d.services.broker_api?.cache_stats);
  const mdMap = asCacheMap(d.services.market_data?.cache_stats);
  const cacheLoaded = Object.keys(brokerMap).length > 0 && Object.keys(mdMap).length > 0;
  const drift = serviceDrift(d.services.broker_api) || serviceDrift(d.services.market_data);
  const cache: Health = drift
    ? 'degraded'
    : cacheLoaded ? 'healthy' : (Object.keys(brokerMap).length || Object.keys(mdMap).length) ? 'degraded' : 'critical';

  return [
    { key: 'download', label: 'Master Download', icon: <Download className="w-4 h-4" />, status: download, detail: refreshStates.map((s, i) => `${REFRESH_BROKERS[i]}:${s ?? '—'}`).join('  ') },
    { key: 's3', label: 'S3 Master Files', icon: <HardDrive className="w-4 h-4" />, status: s3, detail: REFRESH_BROKERS.map((b) => `${b}:${d.s3[b]?.exists ? '✓' : '✗'}`).join('  ') },
    { key: 'cache', label: 'ECS Cache', icon: <Database className="w-4 h-4" />, status: cache, detail: drift ? 'replica drift — reload' : `broker-api:${Object.keys(brokerMap).length}  md:${Object.keys(mdMap).length} namespaces` },
    { key: 'broker_api', label: 'Broker API', icon: <Server className="w-4 h-4" />, status: brokerApi, detail: brokerHealth ?? 'no response' },
    { key: 'market_data', label: 'Market Data API', icon: <Server className="w-4 h-4" />, status: marketData, detail: mdHealth ?? 'no response' },
  ];
}

function fmtBytes(n?: number): string {
  if (!n) return '—';
  if (n >= 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  if (n >= 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${n} B`;
}

/** Union of exchanges across the master manifest + both ECS caches for one broker. */
function exchangeBreakdown(
  master?: Record<string, number>,
  broker?: Record<string, number>,
  md?: Record<string, number>,
): { exch: string; master?: number; broker?: number; md?: number }[] {
  const m = master || {}, b = broker || {}, d = md || {};
  const exchanges = Array.from(new Set([...Object.keys(m), ...Object.keys(b), ...Object.keys(d)])).sort();
  return exchanges.map((exch) => ({ exch, master: m[exch], broker: b[exch], md: d[exch] }));
}

// ───────────────────────── page ─────────────────────────
const SuperAdminDataHealthPage: React.FC = () => {
  const { isSuperAdmin, isLoading: authLoading } = useAuth();
  const { showSuccess, showError } = useToast();
  const { askConfirm, confirmDialog } = useConfirm();

  const [data, setData] = useState<DataHealth | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [searchTarget, setSearchTarget] = useState<{ broker: string; exchange: string; service: string } | null>(null);

  const toggleExpanded = useCallback((b: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(b)) next.delete(b); else next.add(b);
      return next;
    });
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await dataHealthService.getHealth());
    } catch (e: any) {
      setError(e?.message || 'Failed to load data health');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isSuperAdmin) load();
  }, [isSuperAdmin, load]);

  const statuses = useMemo(() => deriveStatuses(data), [data]);

  const onRefreshMaster = useCallback(async (broker: string) => {
    if (!(await askConfirm({
      title: `Download ${broker} master?`,
      message: `This re-downloads the ${broker} instrument master from the broker and rewrites it to S3. It does not reload the ECS caches — use "Reload cache" after.`,
      confirmText: 'Download',
      variant: 'warning',
    }))) return;
    setBusy(`refresh-${broker}`);
    try {
      await dataHealthService.refreshMaster(broker);
      showSuccess(`${broker} master download triggered`);
      await load();
    } catch (e: any) {
      showError(e?.message || `Failed to download ${broker} master`);
    } finally {
      setBusy(null);
    }
  }, [askConfirm, showSuccess, showError, load]);

  const onReloadCache = useCallback(async (service: string) => {
    if (!(await askConfirm({
      title: `Reload ${service} cache?`,
      message: `Broadcasts a reload so every ${service} worker process (across all tasks) re-reads the instrument master from S3 into memory. Brief CPU spike; LTP lookups continue against the old cache until each worker's swap completes.`,
      confirmText: 'Reload',
      variant: 'warning',
    }))) return;
    setBusy(`reload-${service}`);
    try {
      const r = await dataHealthService.reloadCache(service);
      const n = r?.result?.workers_notified;
      showSuccess(`${service}: reload broadcast to ${n ? `${n} worker${n === 1 ? '' : 's'}` : 'all workers'}`);
      await load();
    } catch (e: any) {
      showError(e?.message || `Failed to reload ${service} cache`);
    } finally {
      setBusy(null);
    }
  }, [askConfirm, showSuccess, showError, load]);

  if (authLoading) return null;
  if (!isSuperAdmin) return <Navigate to="/" replace />;

  const brokerMap = asCacheMap(data?.services.broker_api?.cache_stats);
  const mdMap = asCacheMap(data?.services.market_data?.cache_stats);

  return (
    <StandardLayout>
      <PageHeader
        title="Data Pipeline Health"
        pageType="management"
        icon={<Activity className="w-5 h-5" />}
        breadcrumbs={[
          { label: 'Home', href: '/', icon: <Home className="w-4 h-4" /> },
          { label: 'Platform Owner' },
          { label: 'Data Pipeline' },
        ]}
        actions={(
          <Button variant="outline" size="sm" leftIcon={<RefreshCw className={cn('w-4 h-4', loading && 'animate-spin')} />} onClick={load} disabled={loading}>
            Refresh
          </Button>
        )}
      />

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 text-red-800 dark:text-red-200">
          {error}
        </div>
      )}

      {/* 1 — status band */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {(loading && !data ? [] : statuses).map((s) => (
          <div key={s.key} className={cn('rounded-lg border p-4', PILL[s.status])}>
            <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wider">
              {s.icon}{s.label}
            </div>
            <div className="text-lg font-bold mt-1 capitalize">{s.status}</div>
            <div className="text-[11px] mt-1 opacity-80 truncate" title={s.detail}>{s.detail}</div>
          </div>
        ))}
        {loading && !data && (
          <div className="col-span-2 sm:col-span-5 flex items-center justify-center py-12">
            <RefreshCw className="h-8 w-8 animate-spin text-blue-600" />
            <span className="ml-2 text-gray-600 dark:text-gray-400">Loading…</span>
          </div>
        )}
      </div>

      {/* 2 — per-broker segment table */}
      <Card>
        <CardHeader>
          <CardTitle>Instrument Segments ({REFRESH_BROKERS.length})</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-700 border-y border-gray-200 dark:border-gray-700">
                <tr className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                  <th className="text-left px-6 py-3">Broker</th>
                  <th className="text-right px-6 py-3">Master Total</th>
                  <th className="text-right px-6 py-3">Broker-API Cache</th>
                  <th className="text-right px-6 py-3">Market-Data Cache</th>
                  <th className="text-left px-6 py-3">S3 Object</th>
                  <th className="text-left px-6 py-3">Last Refresh</th>
                </tr>
              </thead>
              <tbody>
                {REFRESH_BROKERS.map((b) => {
                  const m = data?.refresh[b];
                  const s3 = data?.s3[b];
                  const bc = cacheCount(brokerMap[b]);
                  const mc = cacheCount(mdMap[b]);
                  const isOpen = expanded.has(b);
                  const rows = exchangeBreakdown(m?.by_exchange, brokerMap[b]?.by_exchange, mdMap[b]?.by_exchange);
                  return (
                    <React.Fragment key={b}>
                      <tr
                        className={cn('bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 border-b border-gray-100 dark:border-gray-700/50',
                          rows.length && 'cursor-pointer')}
                        onClick={() => rows.length && toggleExpanded(b)}
                      >
                        <td className="px-6 py-4 font-medium text-gray-900 dark:text-white capitalize">
                          <span className="inline-flex items-center gap-1.5">
                            {rows.length
                              ? (isOpen ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />)
                              : <span className="w-4 h-4 inline-block" />}
                            {b}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right tabular-nums">{m?.total?.toLocaleString() ?? '—'}</td>
                        <td className="px-6 py-4 text-right tabular-nums">{bc?.toLocaleString() ?? '—'}</td>
                        <td className="px-6 py-4 text-right tabular-nums">{mc?.toLocaleString() ?? '—'}</td>
                        <td className="px-6 py-4 text-gray-600 dark:text-gray-300">
                          {s3?.exists ? `${fmtBytes(s3.size_bytes)}` : <span className="text-red-600 dark:text-red-400">missing</span>}
                        </td>
                        <td className="px-6 py-4 text-gray-600 dark:text-gray-300">{m?.timestamp ? new Date(m.timestamp).toLocaleString() : '—'}</td>
                      </tr>
                      {isOpen && rows.length > 0 && (
                        <tr className="bg-gray-50/60 dark:bg-gray-900/30 border-b border-gray-100 dark:border-gray-700/50">
                          <td colSpan={6} className="px-6 py-3">
                            <div className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">
                              {b} — per-exchange breakdown ({rows.length})
                            </div>
                            <table className="text-sm w-full max-w-xl">
                              <thead>
                                <tr className="text-[11px] uppercase tracking-wider text-gray-400 dark:text-gray-500">
                                  <th className="text-left py-1 pr-4">Exchange</th>
                                  <th className="text-right py-1 px-4">Master</th>
                                  <th className="text-right py-1 px-4">Broker-API</th>
                                  <th className="text-right py-1 pl-4">Market-Data</th>
                                </tr>
                              </thead>
                              <tbody>
                                {rows.map((r) => {
                                  const svc = r.broker != null ? 'broker_api' : (r.md != null ? 'market_data' : null);
                                  return (
                                    <tr
                                      key={r.exch}
                                      className={cn('border-t border-gray-200 dark:border-gray-700/50',
                                        svc && 'cursor-pointer hover:bg-blue-50 dark:hover:bg-blue-900/20')}
                                      onClick={(e) => { e.stopPropagation(); if (svc) setSearchTarget({ broker: b, exchange: r.exch, service: svc }); }}
                                      title={svc ? `Search ${b} ${r.exch} instruments` : undefined}
                                    >
                                      <td className="text-left py-1.5 pr-4 font-medium text-gray-700 dark:text-gray-200">
                                        <span className="inline-flex items-center gap-1.5">
                                          {svc && <Search className="w-3 h-3 text-gray-400" />}{r.exch}
                                        </span>
                                      </td>
                                      <td className="text-right py-1.5 px-4 tabular-nums text-gray-600 dark:text-gray-300">{r.master?.toLocaleString() ?? '—'}</td>
                                      <td className="text-right py-1.5 px-4 tabular-nums text-gray-600 dark:text-gray-300">{r.broker?.toLocaleString() ?? '—'}</td>
                                      <td className="text-right py-1.5 pl-4 tabular-nums text-gray-600 dark:text-gray-300">{r.md?.toLocaleString() ?? '—'}</td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* 2b — per-task replicas (drift detection) */}
      {data && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <ReplicaCard title="Broker-API replicas" svc={data.services.broker_api} />
          <ReplicaCard title="Market-Data replicas" svc={data.services.market_data} />
        </div>
      )}

      {/* 3 + 4 — LTP tester & operator actions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <LtpTester />
        <Card>
          <CardHeader>
            <CardTitle>Operator Actions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <div className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Download instrument master</div>
              <div className="flex flex-wrap gap-2">
                {REFRESH_BROKERS.map((b) => (
                  <Button key={b} variant="secondary" size="sm" isLoading={busy === `refresh-${b}`} disabled={!!busy}
                    leftIcon={<Download className="w-4 h-4" />} onClick={() => onRefreshMaster(b)}>
                    {b}
                  </Button>
                ))}
              </div>
            </div>
            <div>
              <div className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Reload ECS cache from S3</div>
              <div className="flex flex-wrap gap-2">
                {(['broker_api', 'market_data'] as const).map((svc) => (
                  <Button key={svc} variant="secondary" size="sm" isLoading={busy === `reload-${svc}`} disabled={!!busy}
                    leftIcon={<RefreshCw className="w-4 h-4" />} onClick={() => onReloadCache(svc)}>
                    {svc.replace('_', '-')}
                  </Button>
                ))}
              </div>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Typical recovery: download the master, then reload the affected service's cache. The status band above refreshes automatically after each action.
            </p>
          </CardContent>
        </Card>
      </div>

      {/* 5 — direct broker order placement (SuperAdmin ops / algo_id verification) */}
      <DirectOrderPlacer />

      {data?.generated_at && (
        <div className="text-xs text-gray-400 dark:text-gray-500 text-right">Snapshot: {data.generated_at}</div>
      )}
      {confirmDialog}
      <InstrumentSearchDialog target={searchTarget} onClose={() => setSearchTarget(null)} />
    </StandardLayout>
  );
};

// ───────────────────────── per-task replicas ─────────────────────────
const ReplicaCard: React.FC<{ title: string; svc?: ServiceHealth }> = ({ title, svc }) => {
  const tasks = svc?.tasks || [];
  const drift = serviceDrift(svc);
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>{title} ({svc?.via === 'tasks' ? tasks.length : 'ALB'})</CardTitle>
          {drift && (
            <span className={cn('inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase', PILL.degraded)}>
              drift
            </span>
          )}
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {svc?.via !== 'tasks' ? (
          <div className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
            Per-task view unavailable — showing the ALB single-replica response. (ECS task discovery returned nothing.)
          </div>
        ) : tasks.length === 0 ? (
          <div className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">No running tasks.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-700/50">
              <tr className="text-[11px] uppercase tracking-wider text-gray-500 dark:text-gray-400">
                <th className="text-left px-6 py-2">Task</th>
                <th className="text-left px-4 py-2">Health</th>
                <th className="text-right px-4 py-2">Instruments</th>
                <th className="text-left px-6 py-2" title="Sampled from whichever worker process answered — a task runs several workers, each with its own load time">Loaded*</th>
              </tr>
            </thead>
            <tbody>
              {tasks.map((t) => {
                const { total, loadedAt } = taskSummary(t);
                const stale = t.ok && total === 0;
                return (
                  <tr key={t.task_id} className={cn('border-t border-gray-100 dark:border-gray-700/50',
                    !t.ok && 'bg-red-50/60 dark:bg-red-900/10', stale && 'bg-yellow-50/60 dark:bg-yellow-900/10')}>
                    <td className="px-6 py-2 font-mono text-xs text-gray-700 dark:text-gray-200" title={t.ip}>
                      {t.task_id.slice(0, 12)}
                    </td>
                    <td className="px-4 py-2">
                      {t.ok
                        ? <span className="text-green-600 dark:text-green-400">{t.health?.status || 'ok'}</span>
                        : <span className="text-red-600 dark:text-red-400" title={t.error}>unreachable</span>}
                    </td>
                    <td className={cn('px-4 py-2 text-right tabular-nums',
                      stale ? 'text-yellow-700 dark:text-yellow-400 font-semibold' : 'text-gray-600 dark:text-gray-300')}>
                      {t.ok ? total.toLocaleString() : '—'}
                    </td>
                    <td className="px-6 py-2 text-gray-500 dark:text-gray-400 text-xs">
                      {loadedAt ? new Date(loadedAt).toLocaleString() : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        {svc?.via === 'tasks' && tasks.length > 0 && (
          <p className="px-6 py-2 text-[11px] text-gray-400 dark:text-gray-500 border-t border-gray-100 dark:border-gray-700/50">
            * Each task runs several worker processes; the instrument count is authoritative,
            but the load time is sampled from whichever worker answered (it varies between refreshes).
          </p>
        )}
      </CardContent>
    </Card>
  );
};

// ───────────────────────── instrument search dialog ─────────────────────────
const InstrumentSearchDialog: React.FC<{
  target: { broker: string; exchange: string; service: string } | null;
  onClose: () => void;
}> = ({ target, onClose }) => {
  const [query, setQuery] = useState('');
  const [result, setResult] = useState<InstrumentSearchResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset when a different exchange opens the dialog.
  useEffect(() => {
    setQuery(''); setResult(null); setError(null);
  }, [target?.broker, target?.exchange, target?.service]);

  // Debounced server-side search (re-fires on query change while open).
  useEffect(() => {
    if (!target) return;
    let cancelled = false;
    setLoading(true); setError(null);
    const t = setTimeout(async () => {
      try {
        const r = await dataHealthService.searchInstruments({ ...target, query, limit: 100 });
        if (!cancelled) setResult(r);
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Search failed');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [target, query]);

  if (!target) return null;
  const svcLabel = target.service === 'broker_api' ? 'Broker-API' : 'Market-Data';

  return (
    <Modal isOpen onClose={onClose} maxWidth="2xl" ariaLabel={`${target.broker} ${target.exchange} instruments`}>
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-start justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white capitalize">
            {target.broker} · {target.exchange} instruments
          </h2>
          <p className="text-xs text-gray-500 dark:text-gray-400">{svcLabel} in-memory cache</p>
        </div>
        <button onClick={onClose} aria-label="Close" className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400">
          <X className="w-5 h-5" />
        </button>
      </div>
      <div className="px-6 py-3 border-b border-gray-200 dark:border-gray-700">
        <div className="relative">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            autoFocus value={query} onChange={(e) => setQuery(e.target.value.toUpperCase())}
            placeholder="Search trading symbol…"
            className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white rounded-md pl-9 pr-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 placeholder-gray-500 dark:placeholder-gray-400"
          />
        </div>
      </div>
      <div className="flex-1 overflow-y-auto">
        {error && (
          <div className="px-6 py-4 text-red-600 dark:text-red-400 text-sm">{error}</div>
        )}
        {result && (
          <>
            <div className="px-6 py-2 text-xs text-gray-500 dark:text-gray-400 flex items-center gap-2">
              {loading && <RefreshCw className="w-3 h-3 animate-spin" />}
              Showing {result.matches.length.toLocaleString()} of {result.total.toLocaleString()} match{result.total === 1 ? '' : 'es'}
              {result.total > result.matches.length && ' — refine the search to narrow'}
            </div>
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-700/50 sticky top-0">
                <tr className="text-[11px] uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  <th className="text-left px-6 py-2">Trading Symbol</th>
                  <th className="text-right px-6 py-2">Token</th>
                </tr>
              </thead>
              <tbody>
                {result.matches.map((m) => (
                  <tr key={`${m.symbol}-${m.token}`} className="border-t border-gray-100 dark:border-gray-700/50">
                    <td className="text-left px-6 py-1.5 font-mono text-gray-800 dark:text-gray-100">{m.symbol}</td>
                    <td className="text-right px-6 py-1.5 tabular-nums text-gray-600 dark:text-gray-300">{String(m.token)}</td>
                  </tr>
                ))}
                {result.matches.length === 0 && (
                  <tr><td colSpan={2} className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">No instruments match “{query}”.</td></tr>
                )}
              </tbody>
            </table>
          </>
        )}
        {loading && !result && (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="h-6 w-6 animate-spin text-blue-600" />
          </div>
        )}
      </div>
    </Modal>
  );
};

// ───────────────────────── LTP tester ─────────────────────────
const BROKERS = Object.values(BROKER_NAME);

const LtpTester: React.FC = () => {
  const { showError } = useToast();
  const [broker, setBroker] = useState<string>(BROKER_NAME.ZEBU);
  const [userId, setUserId] = useState('');
  const [clientId, setClientId] = useState('');
  const [exchange, setExchange] = useState('NFO');
  const [symbol, setSymbol] = useState('');
  const [result, setResult] = useState<LtpTestResult | null>(null);
  const [testing, setTesting] = useState(false);

  const inputCls = FORM_INPUT_CLS;

  const onTest = async () => {
    if (!userId || !clientId || !exchange || !symbol) {
      showError('user_id, client_id, exchange and symbol are required');
      return;
    }
    setTesting(true);
    setResult(null);
    try {
      setResult(await dataHealthService.testLtp({ broker, user_id: userId, client_id: clientId, exchange, symbol }));
    } catch (e: any) {
      showError(e?.message || 'LTP test failed');
    } finally {
      setTesting(false);
    }
  };

  const leg = (label: string, ok?: boolean, price?: number, err?: string) => (
    <div className={cn('rounded-md border p-3', ok ? PILL.healthy : ok === false ? PILL.critical : PILL.unknown)}>
      <div className="text-xs font-medium uppercase tracking-wider flex items-center gap-1">
        {ok ? <CheckCircle2 className="w-3.5 h-3.5" /> : null}{label}
      </div>
      <div className="text-xl font-bold tabular-nums mt-1">{ok ? (price ?? '—') : 'ERR'}</div>
      {err && <div className="text-[11px] mt-1 opacity-80 truncate" title={err}>{err}</div>}
    </div>
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>LTP Probe (broker vs market-data)</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-2 gap-2">
          <select value={broker} onChange={(e) => setBroker(e.target.value)} className={inputCls}>
            {BROKERS.map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
          <input value={exchange} onChange={(e) => setExchange(e.target.value.toUpperCase())} placeholder="Exchange (NFO)" className={inputCls} />
          <input value={userId} onChange={(e) => setUserId(e.target.value)} placeholder="user_id" className={inputCls} />
          <input value={clientId} onChange={(e) => setClientId(e.target.value)} placeholder="client_id" className={inputCls} />
          <input value={symbol} onChange={(e) => setSymbol(e.target.value)} placeholder="Trading symbol" className={cn(inputCls, 'col-span-2')} />
        </div>
        <Button variant="primary" size="sm" isLoading={testing} onClick={onTest}>Run LTP test</Button>
        {result && (
          <div className="grid grid-cols-2 gap-2 pt-1">
            {leg('Broker LTP', result.broker_ltp.ok, result.broker_ltp.ltp, result.broker_ltp.error)}
            {leg('Market-Data LTP', result.md_ltp.ok, result.md_ltp.price, result.md_ltp.error)}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ───────────────────────── direct order placement ─────────────────────────
// SuperAdmin ops tool: place a single REGULAR LIMIT order straight through the
// broker-API, bypassing the orchestration engine. The order is NOT
// platform-tracked (no SL / auto-exit). Primary use: verify the marketplace
// algo_id resolution end-to-end without needing a full strategy/subscription.
const DirectOrderPlacer: React.FC = () => {
  const { showError, showSuccess, showWarning } = useToast();
  const { askConfirm, confirmDialog } = useConfirm();
  const [clientId, setClientId] = useState('');
  const [broker, setBroker] = useState<string>(BROKER_NAME.ZEBU);
  const [exchange, setExchange] = useState<string>(EXCHANGE.NFO);
  const [symbol, setSymbol] = useState('');
  const [transactionType, setTransactionType] = useState<string>(ACTION_TYPE.BUY);
  const [product, setProduct] = useState<string>(PRODUCT_TYPE.NRML);
  const [quantity, setQuantity] = useState('');
  const [limitPrice, setLimitPrice] = useState('');
  const [tag, setTag] = useState('SUPERADMIN_TEST');
  const [isMarketplace, setIsMarketplace] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<PlaceOrderResult | null>(null);

  const inputCls = FORM_INPUT_CLS;
  const labelCls = 'block text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1';

  const qty = Number(quantity);
  const price = Number(limitPrice);

  const onPlace = async () => {
    if (!clientId.trim() || !symbol.trim()) {
      showError('client_id and symbol are required');
      return;
    }
    if (!Number.isInteger(qty) || qty <= 0) {
      showError('quantity must be a whole number greater than 0');
      return;
    }
    if (!Number.isFinite(price) || price <= 0) {
      showError('limit price must be greater than 0');
      return;
    }
    const ok = await askConfirm({
      title: 'Place this order at the broker?',
      // JSX (not a \n string) so the lines — and the no-SL warning — render as
      // distinct blocks; a plain string collapses newlines into one paragraph.
      message: (
        <div className="space-y-2">
          <div>{transactionType} {qty} × {symbol.trim()} @ ₹{price} (LIMIT, {product}) on {exchange}</div>
          <div>Account: {broker} / {clientId.trim()} · marketplace algo_id: {isMarketplace ? 'YES' : 'no'}</div>
          <div className="font-semibold text-red-600 dark:text-red-400">
            This sends a REAL order to the broker and is NOT tracked by the platform — no stop-loss and no auto-exit will be attached. Manage/exit it yourself from the broker terminal.
          </div>
        </div>
      ),
      confirmText: `Place ${transactionType}`,
      variant: 'danger',
    });
    if (!ok) return;
    setSubmitting(true);
    setResult(null);
    try {
      const r = await dataHealthService.placeOrder({
        client_id: clientId.trim(), broker, exchange, symbol: symbol.trim(),
        transaction_type: transactionType, product, quantity: qty,
        limit_price: price, tag: tag.trim(), is_marketplace: isMarketplace,
      });
      setResult(r);
      // Tri-state: placed / uncertain (may be live — verify before retry) / rejected.
      if (r.order_placed) {
        showSuccess(`Order placed for ${r.client_id} (${r.broker})`);
        // Clear the order-defining fields so a reflexive re-submit can't fire a
        // duplicate identical order.
        setSymbol(''); setQuantity(''); setLimitPrice('');
      } else if (r.order_uncertain) {
        showWarning(`Order status UNCERTAIN — it may be live at ${r.broker}. Verify at the broker terminal before retrying${r.error_message ? ` (${r.error_message})` : ''}.`);
      } else {
        showError(`Broker rejected the order${r.error_message ? `: ${r.error_message}` : ''}`);
      }
    } catch (e: any) {
      // apiClient rejects non-2xx with the raw AxiosError; the backend reason
      // lives at response.data.error, not the generic axios message.
      showError(e?.response?.data?.error || e?.message || 'Order placement failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Direct Order Placement</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md p-3 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 text-yellow-600 dark:text-yellow-400 mt-0.5 shrink-0" />
          <p className="text-xs text-yellow-800 dark:text-yellow-200">
            Sends a <strong>real REGULAR LIMIT</strong> order straight to the broker — the account must be logged in.
            The order is <strong>not platform-tracked</strong> (no SL / auto-exit). Use for ops and to verify the
            marketplace <code>algo_id</code>.
          </p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <div>
            <label className={labelCls}>Broker</label>
            <select value={broker} onChange={(e) => setBroker(e.target.value)} className={inputCls}>
              {BROKERS.map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>Client ID</label>
            <input value={clientId} onChange={(e) => setClientId(e.target.value.toUpperCase())} placeholder="Z59828" className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Exchange</label>
            <select value={exchange} onChange={(e) => setExchange(e.target.value)} className={inputCls}>
              {EXCHANGE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>Side</label>
            <select value={transactionType} onChange={(e) => setTransactionType(e.target.value)} className={inputCls}>
              {[ACTION_TYPE.BUY, ACTION_TYPE.SELL].map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
          <div className="col-span-2 sm:col-span-3">
            <label className={labelCls}>Option Symbol</label>
            <input value={symbol} onChange={(e) => setSymbol(e.target.value.toUpperCase())} placeholder="NIFTY2572424150CE" className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Product</label>
            <select value={product} onChange={(e) => setProduct(e.target.value)} className={inputCls}>
              {PRODUCT_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>Quantity</label>
            <input type="number" min={1} value={quantity} onChange={(e) => setQuantity(e.target.value)} placeholder="qty (lots × lot size)" className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Limit Price</label>
            <input type="number" min={0} step="0.05" value={limitPrice} onChange={(e) => setLimitPrice(e.target.value)} placeholder="0.00" className={inputCls} />
          </div>
          <div className="col-span-2">
            <label className={labelCls}>Tag</label>
            <input value={tag} onChange={(e) => setTag(e.target.value)} placeholder="SUPERADMIN_TEST" className={inputCls} />
          </div>
          <div className="flex items-end pb-2">
            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200 cursor-pointer">
              <input type="checkbox" checked={isMarketplace} onChange={(e) => setIsMarketplace(e.target.checked)}
                className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500" />
              Marketplace algo_id
            </label>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <Button variant="primary" size="sm" isLoading={submitting} leftIcon={<Send className="w-4 h-4" />} onClick={onPlace}>
            Place order
          </Button>
          <span className="text-xs text-gray-500 dark:text-gray-400">Order type: <strong>LIMIT</strong> · Variety: <strong>REGULAR</strong> (fixed)</span>
        </div>

        {result && (
          <div className={cn('rounded-md border p-3 space-y-1',
            result.order_placed
              ? 'border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20'
              : result.order_uncertain
                ? 'border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20'
                : 'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20')}>
            <div className={cn('text-xs font-medium uppercase tracking-wider flex items-center gap-1',
              result.order_placed
                ? 'text-green-800 dark:text-green-200'
                : result.order_uncertain ? 'text-amber-800 dark:text-amber-200' : 'text-red-800 dark:text-red-200')}>
              {result.order_placed
                ? <><CheckCircle2 className="w-3.5 h-3.5" /> Order placed — {result.broker} / {result.client_id}</>
                : result.order_uncertain
                  ? <><AlertTriangle className="w-3.5 h-3.5" /> Status uncertain — may be live at {result.broker}. Verify before retrying.</>
                  : <><AlertTriangle className="w-3.5 h-3.5" /> Broker rejected — {result.broker} / {result.client_id}</>}
            </div>
            {!result.order_placed && result.error_message && (
              <div className={cn('text-xs', result.order_uncertain ? 'text-amber-700 dark:text-amber-300' : 'text-red-700 dark:text-red-300')}>{result.error_message}</div>
            )}
            <div className="text-xs text-gray-700 dark:text-gray-300">
              {result.transaction_type} {result.quantity} × {result.symbol} @ ₹{result.limit_price} · {result.exchange} · marketplace: {result.is_marketplace ? 'yes' : 'no'}
            </div>
            <pre className="text-[11px] bg-white/60 dark:bg-black/20 rounded p-2 overflow-x-auto text-gray-700 dark:text-gray-300">
              {JSON.stringify(result.result, null, 2)}
            </pre>
          </div>
        )}
        {confirmDialog}
      </CardContent>
    </Card>
  );
};

export default SuperAdminDataHealthPage;
